import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Database, 
  Trash2, 
  RefreshCw, 
  Clock, 
  HardDrive,
  Shield,
  AlertTriangle,
  CheckCircle,
  Calendar,
  Play,
  Cloud,
  CloudOff,
  RotateCcw,
  Download
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BackupManagement = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [backups, setBackups] = useState([]);
  const [status, setStatus] = useState(null);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [safeRestore, setSafeRestore] = useState(true);
  const [confirmRestore, setConfirmRestore] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [backupsRes, statusRes] = await Promise.all([
        axios.get(`${API}/api/admin/backups`, { headers }),
        axios.get(`${API}/api/admin/backups/status`, { headers })
      ]);
      setBackups(backupsRes.data.backups);
      setStatus(statusRes.data);
    } catch (error) {
      console.error('Error fetching backup data:', error);
      toast.error('Failed to load backup data');
    } finally {
      setLoading(false);
    }
  };

  const createBackup = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api/admin/backups/create?encrypt=true`, {}, { headers });
      toast.success(`Backup created and uploaded to Backblaze B2: ${res.data.filename}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create backup');
    } finally {
      setCreating(false);
    }
  };

  const deleteBackup = async (filename, fileId) => {
    if (!window.confirm(`Delete ${filename} from Backblaze B2?`)) return;
    
    try {
      const url = fileId 
        ? `${API}/api/admin/backups/${encodeURIComponent(filename)}?file_id=${fileId}`
        : `${API}/api/admin/backups/${encodeURIComponent(filename)}`;
      await axios.delete(url, { headers });
      toast.success('Backup deleted from Backblaze B2');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete backup');
    }
  };

  const initiateRestore = (backup) => {
    setSelectedBackup(backup);
    setSafeRestore(true);
    setConfirmRestore(false);
    setShowRestoreDialog(true);
  };

  const restoreBackup = async () => {
    if (!confirmRestore) {
      toast.error('Please confirm you understand the restore process');
      return;
    }
    
    setRestoring(true);
    try {
      const res = await axios.post(`${API}/api/admin/backups/restore`, {
        filename: selectedBackup.filename,
        location: "b2",
        safe_restore: safeRestore,
        file_id: selectedBackup.file_id
      }, { headers });
      
      toast.success(res.data.message);
      setShowRestoreDialog(false);
      setSelectedBackup(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoring(false);
    }
  };

  const getBackupTypeColor = (type) => {
    switch (type) {
      case 'daily': return 'bg-blue-100 text-blue-700';
      case 'weekly': return 'bg-purple-100 text-purple-700';
      case 'monthly': return 'bg-green-100 text-green-700';
      case 'manual': return 'bg-amber-100 text-amber-700';
      case 'pre_restore': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const formatDate = (dateStr) => {
    // dd/MM/yyyy HH:mm in NZ time
    return new Intl.DateTimeFormat('en-NZ', {
      timeZone: 'Pacific/Auckland',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(dateStr));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen" data-testid="backup-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 p-4 md:p-6 lg:p-8" data-testid="backup-management-page">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
              <Database className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Database Backups</h1>
              <p className="text-slate-500">Automated backups synced to Backblaze B2</p>
            </div>
          </div>
          <Button 
            onClick={createBackup} 
            disabled={creating}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="create-backup-btn"
          >
            {creating ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            {creating ? 'Creating...' : 'Create Backup'}
          </Button>
        </div>

        {/* Environment tag banner — makes it obvious which environment's backups you're viewing */}
        {status?.backup_env && (
          <div
            className={`rounded-lg px-4 py-2 text-sm font-semibold flex items-center justify-between ${
              status.backup_env === 'production'
                ? 'bg-red-50 text-red-800 border border-red-200'
                : 'bg-amber-50 text-amber-800 border border-amber-200'
            }`}
            data-testid="backup-env-banner"
          >
            <span>
              Environment: <span className="uppercase tracking-wide">{status.backup_env}</span>
              <span className="ml-3 text-xs font-normal opacity-80">
                Only {status.backup_env} backups are shown. Cross-environment restore is blocked.
              </span>
            </span>
            <span className="text-xs font-normal">
              Auto-schedule: {status.scheduler_enabled ? 'ON (2 AM NZ)' : 'OFF'}
            </span>
          </div>
        )}

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card className="border-0 shadow-md" data-testid="b2-status-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${status?.b2_connected ? 'bg-green-100' : 'bg-red-100'}`}>
                  {status?.b2_connected ? (
                    <Cloud className="w-5 h-5 text-green-600" />
                  ) : (
                    <CloudOff className="w-5 h-5 text-red-600" />
                  )}
                </div>
                <div>
                  <p className="text-sm text-slate-500">Backblaze B2</p>
                  <p className={`font-bold ${status?.b2_connected ? 'text-green-600' : 'text-red-600'}`}>
                    {status?.b2_connected ? 'Connected' : 'Disconnected'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <HardDrive className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Size</p>
                  <p className="font-bold">{status?.total_backup_size || '0 B'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Shield className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Encryption</p>
                  <p className="font-bold text-purple-600">AES-256</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <RotateCcw className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Safe Restore</p>
                  <p className="font-bold text-emerald-600">Enabled</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Calendar className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Backups</p>
                  <p className="font-bold">{backups.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* B2 Bucket Info */}
        {status?.b2_storage && status.b2_storage.bucket_name && (
          <Card className="border-0 shadow-md bg-gradient-to-r from-sky-50 to-blue-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Cloud className="w-5 h-5 text-sky-600" />
                  <div>
                    <p className="font-medium text-sky-800">Backblaze B2 Bucket: {status.b2_storage.bucket_name}</p>
                    <p className="text-sm text-sky-600">
                      Type: {status.b2_storage.bucket_type} • Prefix: {status.b2_prefix}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Retention Policy & Schedule */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="w-5 h-5 text-indigo-600" />
              Backup Schedule & Retention
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4 mb-4">
              <div className="flex items-center gap-2">
                <Badge className="bg-blue-100 text-blue-700">Daily</Badge>
                <span className="text-sm">Keep {status?.retention_policy?.daily || 7}</span>
                <span className="text-xs text-slate-400">({status?.backup_counts?.daily || 0} stored)</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-purple-100 text-purple-700">Weekly</Badge>
                <span className="text-sm">Keep {status?.retention_policy?.weekly || 4}</span>
                <span className="text-xs text-slate-400">({status?.backup_counts?.weekly || 0} stored)</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-green-100 text-green-700">Monthly</Badge>
                <span className="text-sm">Keep {status?.retention_policy?.monthly || 3}</span>
                <span className="text-xs text-slate-400">({status?.backup_counts?.monthly || 0} stored)</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-amber-100 text-amber-700">Manual</Badge>
                <span className="text-sm">No auto-delete</span>
                <span className="text-xs text-slate-400">({status?.backup_counts?.manual || 0} stored)</span>
              </div>
            </div>
            <div className="p-3 bg-indigo-50 rounded-lg">
              <p className="text-sm text-indigo-800">
                <strong>Schedule:</strong> Automated backups run daily at 2:00 AM and upload to Backblaze B2 bucket: 
                <code className="bg-indigo-100 px-1.5 py-0.5 rounded ml-1">{status?.b2_bucket}</code>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Safe Restore Info */}
        <Card className="border-0 shadow-lg border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-emerald-600 shrink-0" />
              <div>
                <p className="font-semibold text-emerald-800">Safe Restore Protection</p>
                <p className="text-sm text-slate-600 mt-1">
                  Before any restore operation, the system automatically creates a "pre-restore" backup of your current database. 
                  This ensures you can always roll back if something goes wrong during the restore process.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Backup List */}
        <Card className="border-0 shadow-lg" data-testid="backup-list-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Cloud className="w-5 h-5 text-indigo-600" />
                Backblaze B2 Backups
              </CardTitle>
              <CardDescription>
                Select a backup to restore your database to that point in time
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-backups-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </CardHeader>
          <CardContent>
            {backups.length === 0 ? (
              <div className="text-center py-12 text-slate-500" data-testid="no-backups-message">
                <Cloud className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No backups in Backblaze B2</p>
                <p className="text-sm">Create your first backup using the button above</p>
              </div>
            ) : (
              <div className="space-y-2">
                {backups.map((backup, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    data-testid={`backup-item-${idx}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-white rounded-lg shadow-sm">
                        {backup.encrypted ? (
                          <Shield className="w-5 h-5 text-green-600" />
                        ) : (
                          <Database className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-sm">{backup.filename}</p>
                          <Badge className={getBackupTypeColor(backup.backup_type)}>
                            {backup.backup_type}
                          </Badge>
                          {backup.encrypted && (
                            <Badge className="bg-green-100 text-green-700">Encrypted</Badge>
                          )}
                          <Badge className="bg-sky-100 text-sky-700">
                            <Cloud className="w-3 h-3 mr-1" />
                            B2
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          {formatDate(backup.created_at)} • {backup.size_human}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => initiateRestore(backup)}
                        className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                        data-testid={`restore-btn-${idx}`}
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        Restore
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => deleteBackup(backup.filename, backup.file_id)}
                        data-testid={`delete-btn-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Last Backup Info */}
        {status?.last_backup && (
          <Card className="border-0 shadow-md bg-gradient-to-r from-indigo-50 to-purple-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-indigo-600" />
                <div>
                  <p className="text-sm text-slate-600">Last backup:</p>
                  <p className="font-medium">
                    {status.last_backup.filename} ({status.last_backup.size_human}) - {formatDate(status.last_backup.created_at)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Restore Dialog */}
        <Dialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
          <DialogContent className="max-w-md" data-testid="restore-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-indigo-600" />
                Restore Database
              </DialogTitle>
              <DialogDescription>
                Restore your database to this point in time
              </DialogDescription>
            </DialogHeader>
            {selectedBackup && (
              <div className="space-y-4">
                {/* Selected Backup Info */}
                <div className="p-3 bg-slate-100 rounded-lg">
                  <p className="text-sm font-medium">Restoring from:</p>
                  <p className="text-sm text-slate-600">{selectedBackup.filename}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Created: {formatDate(selectedBackup.created_at)} • Size: {selectedBackup.size_human}
                  </p>
                </div>

                {/* Safe Restore Option */}
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="safe-restore"
                      checked={safeRestore}
                      onCheckedChange={setSafeRestore}
                      data-testid="safe-restore-checkbox"
                    />
                    <div>
                      <label htmlFor="safe-restore" className="font-medium text-emerald-800 cursor-pointer">
                        Safe Restore (Recommended)
                      </label>
                      <p className="text-sm text-emerald-700 mt-1">
                        Creates a backup of your current database before restoring, so you can undo this action if needed.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Warning */}
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">What will happen:</p>
                      <ul className="text-sm text-amber-700 mt-1 space-y-1 list-disc list-inside">
                        {safeRestore && <li>A pre-restore backup will be created first</li>}
                        <li>Current database will be replaced with backup data</li>
                        <li>All changes since {formatDate(selectedBackup.created_at)} will be lost</li>
                        <li>Users may need to log in again</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Confirmation */}
                <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg">
                  <Checkbox
                    id="confirm-restore"
                    checked={confirmRestore}
                    onCheckedChange={setConfirmRestore}
                    data-testid="confirm-restore-checkbox"
                  />
                  <label htmlFor="confirm-restore" className="text-sm text-red-700 cursor-pointer">
                    I understand this will replace my current database and I want to proceed
                  </label>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowRestoreDialog(false)}
                    className="flex-1"
                    data-testid="cancel-restore-btn"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={restoreBackup}
                    disabled={!confirmRestore || restoring}
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                    data-testid="confirm-restore-btn"
                  >
                    {restoring ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Restoring...
                      </>
                    ) : (
                      <>
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Restore Database
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default BackupManagement;
